// Хук для работы с документами

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Document,
  User,
  Storage,
  Material,
  DocumentFormData,
  TableRowData
} from './types';

// Хук для загрузки пользователей/аптек
export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchUsers = useCallback(async (cityId?: string) => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (cityId) {
        params.cityId = cityId;
      }
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy', { params });
      if (response.data.success) {
        setUsers(response.data.users);
        return { success: true };
      } else {
        return { success: false, message: 'Не удалось загрузить список аптек' };
      }
    } catch (error) {
      console.error('Ошибка при загрузке аптек:', error);
      return { success: false, message: 'Ошибка при загрузке аптек' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    users,
    isLoading,
    fetchUsers,
  };
};

// Хук для загрузки складов (для возвратов)
export const useStorages = () => {
  const [storages, setStorages] = useState<Storage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStorages = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('https://tbl.zerdegroup.kz/api/storage-list');
      if (response.data.success) {
        setStorages(response.data.users);
        return { success: true };
      } else {
        return { success: false, message: 'Не удалось загрузить список складов' };
      }
    } catch (error) {
      console.error('Ошибка при загрузке складов:', error);
      return { success: false, message: 'Ошибка при загрузке складов' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    storages,
    isLoading,
    fetchStorages,
  };
};

// Хук для работы с документами
export const useDocuments = (username: string, guid: string) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [incomingDocuments, setIncomingDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Загрузка документов пользователя
  const fetchDocuments = useCallback(async (username: string) => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/documents', {
        params: { username }
      });

      if (response.data.success) {
        const docs = Array.isArray(response.data.documents) ? response.data.documents : [];
        setDocuments(docs);
        return { success: true };
      } else {
        console.error('Ошибка ответа сервера:', response.data);
        setDocuments([]);
        return {
          success: false,
          message: response.data.message || 'Не удалось загрузить документы'
        };
      }
    } catch (error) {
      console.error('Ошибка при загрузке документов:', error);
      setDocuments([]);
      return { success: false, message: 'Ошибка при загрузке документов' };
    }
  }, []);

  // Загрузка входящих документов
  const fetchIncomingDocuments = useCallback(async (guid: string) => {
    try {
      const role = localStorage.getItem('role');
      const cityId = localStorage.getItem('cityId');

      const params: any = {};

      // Для складов используется cityId + guid, для аптек - guid
      if (role === 'storage' && cityId) {
        params.cityId = cityId;
        params.role = role;
        params.guid = guid;
      } else {
        params.guid = guid;
      }

      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/documents/incoming', {
        params
      });

      if (response.data.success) {
        const docs = Array.isArray(response.data.documents) ? response.data.documents : [];
        setIncomingDocuments(docs);
        return { success: true };
      } else {
        console.error('Ошибка ответа сервера (входящие):', response.data);
        setIncomingDocuments([]);
        return {
          success: false,
          message: response.data.message || 'Не удалось загрузить входящие документы'
        };
      }
    } catch (error) {
      console.error('Ошибка при загрузке входящих документов:', error);
      setIncomingDocuments([]);
      return { success: false, message: 'Ошибка при загрузке входящих документов' };
    }
  }, []);

  // Загрузка конкретного документа
  const fetchDocument = useCallback(async (documentId: string, mode?: 'edit') => {
    try {
      const params: any = {};
      if (mode) params.mode = mode;

      const response = await axios.get(`https://tbl.zerdegroup.kz/api/v1/documents/${documentId}`, { params });

      if (response.data.success) {
        return { success: true, document: response.data.document };
      } else {
        return { success: false, message: 'Ошибка при загрузке документа' };
      }
    } catch (error) {
      console.error('Ошибка при загрузке документа:', error);
      return { success: false, message: 'Ошибка при загрузке документа' };
    }
  }, []);

  // Сохранение документа
  const saveDocument = useCallback(async (
    documentData: DocumentFormData,
    isEditMode: boolean = false,
    editingDocumentId: string | null = null
  ) => {
    try {
      let response;
      let successMessage = '';

      if (isEditMode && editingDocumentId) {
        // Обновление существующего документа
        response = await axios.put(`https://tbl.zerdegroup.kz/api/v1/documents/${editingDocumentId}`, documentData);
        successMessage = documentData.status === 9 ? 'Документ успешно сохранен' : 'Документ успешно отправлен в работу';
      } else {
        // Создание нового документа
        response = await axios.post('https://tbl.zerdegroup.kz/api/v1/documents', documentData);
        successMessage = documentData.status === 9 ? 'Документ успешно создан' : 'Документ успешно создан и отправлен в работу';
      }

      if (response.data.success) {
        return {
          success: true,
          message: successMessage,
          documentId: response.data.documentId || editingDocumentId
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Ошибка при сохранении документа'
        };
      }
    } catch (error: any) {
      console.error('Ошибка при сохранении документа:', error);

      if (error.response?.data) {
        console.error('Детали ошибки сервера:', error.response.data);
        return {
          success: false,
          message: error.response.data.message || 'Ошибка при сохранении документа'
        };
      } else {
        return { success: false, message: 'Ошибка при сохранении документа' };
      }
    }
  }, []);

  // Удаление документа
  const deleteDocument = useCallback(async (documentId: string, username: string) => {
    try {
      const response = await axios.delete(`https://tbl.zerdegroup.kz/api/v1/documents/${documentId}`, {
        params: { username }
      });

      if (response.data.success) {
        return { success: true, message: 'Документ успешно удален' };
      } else {
        return {
          success: false,
          message: response.data.message || 'Ошибка при удалении документа'
        };
      }
    } catch (error: any) {
      console.error('Ошибка при удалении документа:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при удалении документа'
      };
    }
  }, []);

  // Перезагрузка всех документов
  const reloadDocuments = useCallback(async () => {
    setIsLoading(true);
    const results = await Promise.all([
      username ? fetchDocuments(username) : Promise.resolve({ success: true }),
      guid ? fetchIncomingDocuments(guid) : Promise.resolve({ success: true })
    ]);
    setIsLoading(false);
    return results.every(result => result.success);
  }, [username, guid, fetchDocuments, fetchIncomingDocuments]);

  return {
    documents,
    incomingDocuments,
    isLoading,
    fetchDocuments,
    fetchIncomingDocuments,
    fetchDocument,
    saveDocument,
    deleteDocument,
    reloadDocuments,
  };
};

// Хук для поиска материалов
export const useMaterialSearch = (pharmacyGuid?: string) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchMaterials = useCallback(async (searchTerm: string, docType?: number) => {
    try {
      if (searchTerm.length < 2) {
        setMaterials([]);
        return { success: true, materials: [] };
      }

      setIsLoading(true);

      const params: any = { search: searchTerm, limit: 20 };
      if (pharmacyGuid) {
        params.pharmacyGuid = pharmacyGuid;
      }

      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/materials', { params });

      if (response.data.success) {
        setMaterials(response.data.materials);
        return { success: true, materials: response.data.materials };
      } else {
        return { success: false, materials: [] };
      }
    } catch (error) {
      console.error('Ошибка при поиске материалов:', error);
      return { success: false, materials: [] };
    } finally {
      setIsLoading(false);
    }
  }, [pharmacyGuid]);

  const clearMaterials = useCallback(() => {
    setMaterials([]);
  }, []);

  return {
    materials,
    isLoading,
    searchMaterials,
    clearMaterials,
  };
};

// Хук для управления формой документа
export const useDocumentForm = () => {
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [materialSearchInputs, setMaterialSearchInputs] = useState<{ [key: string]: string }>({});

  const addTableRow = useCallback(() => {
    const newRow: TableRowData = {
      id: Date.now().toString() + Math.random(),
      MaterialZid: '',
      MaterialCode: '',
      MaterialName: '',
      SerialNumber: '',
      SerialDate: '',
      Quantity: '',
      Quantity1C: '',
      Barcode: '',
      Comment: '',
      selectedMaterial: undefined,
      markCodes: [],
      series: '[]'
    };
    setTableData(prev => [...prev, newRow]);
  }, []);

  const removeTableRow = useCallback((id: string) => {
    setTableData(prev => prev.filter(row => row.id !== id));
  }, []);

  const updateTableRow = useCallback((id: string, field: keyof TableRowData, value: string) => {
    setTableData(prev => prev.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));
  }, []);

  const handleMaterialSelect = useCallback((rowId: string, material: Material) => {
    setTableData(prev => prev.map(row =>
      row.id === rowId ? {
        ...row,
        MaterialZid: material.ProductZid,
        MaterialCode: material.ProductSku,
        MaterialName: material.ProductName,
        Barcode: material.Barcode,
        selectedMaterial: material
      } : row
    ));

    setMaterialSearchInputs(prev => ({
      ...prev,
      [rowId]: material.ProductName
    }));
  }, []);

  const handleMaterialSearchChange = useCallback((rowId: string, value: string) => {
    setMaterialSearchInputs(prev => ({
      ...prev,
      [rowId]: value
    }));

    setTableData(prev => prev.map(row =>
      row.id === rowId ? {
        ...row,
        MaterialZid: '',
        MaterialCode: '',
        MaterialName: value,
        Barcode: '',
        selectedMaterial: undefined
      } : row
    ));
  }, []);

  const resetForm = useCallback(() => {
    setTableData([]);
    setMaterialSearchInputs({});
  }, []);

  const loadDocumentData = useCallback((document: Document) => {
    if (document.tableData && document.tableData.length > 0) {
      const convertedTableData = document.tableData.map(item => ({
        id: item.id,
        MaterialZid: item.MaterialZid,
        MaterialCode: item.MaterialCode,
        MaterialName: item.MaterialName,
        SerialNumber: item.SerialNumber,
        SerialDate: item.SerialDate,
        Quantity: item.Quantity,
        QuantityPcs: (item as any).QuantityPcs || '', // Количество в штуках
        Quantity1C: item.Quantity1C || '',
        Barcode: item.Barcode,
        Comment: item.Comment || '',
        selectedMaterial: item.MaterialZid ? {
          ProductZid: item.MaterialZid,
          ProductSku: item.MaterialCode,
          ProductName: item.MaterialName,
          Barcode: item.Barcode,
          PartExists: (item as any).PartExists ?? false, // Из API или false по умолчанию
          PartCoeff: (item as any).PartCoeff || 0 // Из API или 0 по умолчанию
        } : undefined,
        markCodes: item.markCodes?.map(mc => typeof mc === 'string' ? mc : mc.mark) || [],
        series: typeof (item as any).series === 'string' ? (item as any).series : JSON.stringify((item as any).series || [])
      }));

      setTableData(convertedTableData);

      const searchInputs: { [key: string]: string } = {};
      convertedTableData.forEach(item => {
        if (item.MaterialName) {
          searchInputs[item.id] = item.MaterialName;
        }
      });
      setMaterialSearchInputs(searchInputs);
    } else {
      addTableRow();
    }
  }, [addTableRow]);

  return {
    tableData,
    materialSearchInputs,
    addTableRow,
    removeTableRow,
    updateTableRow,
    handleMaterialSelect,
    handleMaterialSearchChange,
    resetForm,
    loadDocumentData,
    setTableData,
    setMaterialSearchInputs,
  };
};
